import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getDayName } from "./tableAvailabilityUtils";
import { normalizeTimeForUI } from "./timeFormatUtils";
import { isTimeWithinSlot as isTimeWithinSlotShared } from "@/lib/serviceOrder";

export interface ImpactedReservationByTimeSlot {
  id: string;
  date: string;
  dayOfWeek: number;
  dayName: string;
  startTime: string;
  numberOfPeople: number;
  customerName: string;
  tableName: string;
  impactReason: 'time_changed' | 'day_unavailable' | 'slot_inactive';
}

export interface TimeSlotImpact {
  hasImpactedReservations: boolean;
  impactedReservations: ImpactedReservationByTimeSlot[];
  totalCount: number;
}

/**
 * Convertit une heure HH:MM en minutes depuis minuit
 */
function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Vérifie si une heure de réservation est dans un créneau
 */
function isTimeWithinSlot(reservationTime: string, slotStartTime: string, slotEndTime: string): boolean {
  // >= début ET < fin ; un créneau de nuit (fin < début) couvre aussi la portion après minuit
  return isTimeWithinSlotShared(reservationTime, { start: slotStartTime, end: slotEndTime });
}

/**
 * Récupère les settings de réservation pour connaître le mode (slot/time)
 */
async function getReservationSettings(establishmentId?: string): Promise<{
  bookingMode: 'slot' | 'time';
  reservationDurationMinutes: number;
}> {
  if (!establishmentId) {
    return { bookingMode: 'slot', reservationDurationMinutes: 120 };
  }

  const { data } = await supabase
    .from('reservation_settings')
    .select('booking_mode, reservation_duration_minutes')
    .eq('establishment_id', establishmentId)
    .single();

  return {
    bookingMode: (data?.booking_mode as 'slot' | 'time') || 'slot',
    reservationDurationMinutes: data?.reservation_duration_minutes || 120,
  };
}

/**
 * Récupère le nom de la table pour une réservation
 */
async function getTableName(tableId: string | null, combinedTableId: string | null): Promise<string> {
  if (combinedTableId) {
    const { data } = await supabase
      .from('combined_tables')
      .select('name')
      .eq('id', combinedTableId)
      .single();
    return data?.name || 'Table combinée inconnue';
  } else if (tableId) {
    const { data } = await supabase
      .from('tables')
      .select('name')
      .eq('id', tableId)
      .single();
    return data?.name || 'Table inconnue';
  }
  return 'Non assignée';
}

/**
 * Récupère toutes les réservations futures pour un établissement
 */
async function getFutureReservations(establishmentId?: string) {
  const today = format(new Date(), 'yyyy-MM-dd');
  
  let query = supabase
    .from('reservations')
    .select(`
      id,
      date,
      start_time,
      number_of_people,
      table_id,
      combined_table_id,
      customers!inner (
        first_name,
        last_name
      )
    `)
    .gte('date', today)
    .neq('status', 'annulé');

  if (establishmentId) {
    query = query.eq('establishment_id', establishmentId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Transforme les réservations en format d'impact
 */
async function transformToImpactedReservations(
  reservations: Array<{
    id: string;
    date: string;
    start_time: string;
    number_of_people: number;
    table_id: string | null;
    combined_table_id: string | null;
    customers: { first_name: string; last_name: string } | null;
  }>,
  impactReason: 'time_changed' | 'day_unavailable' | 'slot_inactive'
): Promise<ImpactedReservationByTimeSlot[]> {
  return Promise.all(
    reservations.map(async (r) => {
      const tableName = await getTableName(r.table_id, r.combined_table_id);
      return {
        id: r.id,
        date: format(new Date(r.date), 'EEEE d MMMM yyyy', { locale: fr }),
        dayOfWeek: new Date(r.date).getDay(),
        dayName: getDayName(new Date(r.date).getDay()),
        startTime: r.start_time,
        numberOfPeople: r.number_of_people,
        customerName: r.customers ? [r.customers.first_name, r.customers.last_name].filter(Boolean).join(' ') || 'Client inconnu' : 'Client inconnu',
        tableName,
        impactReason,
      };
    })
  );
}

/**
 * Vérifie l'impact d'une modification de créneau horaire sur les réservations futures
 * 
 * MODE TIME: Détecte les réservations dont l'heure est DANS le créneau (>= start ET < end)
 * MODE SLOT: Détecte les réservations dont l'heure correspond exactement au début du créneau
 */
export async function checkTimeSlotModificationImpact(
  slotId: string,
  oldStartTime: string,
  newStartTime: string,
  newAvailableDays: number[],
  establishmentId?: string,
  oldEndTime?: string,
  newEndTime?: string,
  oldAvailableDays?: number[]
): Promise<TimeSlotImpact> {
  try {
    const settings = await getReservationSettings(establishmentId);
    const reservations = await getFutureReservations(establishmentId);

    if (!reservations || reservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
    }

    let impactedReservations: ImpactedReservationByTimeSlot[] = [];

    // Cas 1: Vérifier les réservations "orphelines" (dans l'ancien créneau mais hors du nouveau)
    {
      let affectedReservations: typeof reservations;

      if (settings.bookingMode === 'time' && oldEndTime && newEndTime) {
        // MODE TIME: Chercher les réservations orphelines
        affectedReservations = reservations.filter(r => {
          const wasInOldSlot = isTimeWithinSlot(r.start_time, oldStartTime, oldEndTime);
          const isInNewSlot = isTimeWithinSlot(r.start_time, newStartTime, newEndTime);
          return wasInOldSlot && !isInNewSlot;
        });
      } else {
        // MODE SLOT: Chercher les réservations au début exact si le start_time change
        if (normalizeTimeForUI(oldStartTime) !== normalizeTimeForUI(newStartTime)) {
          affectedReservations = reservations.filter(r => 
            normalizeTimeForUI(r.start_time) === normalizeTimeForUI(oldStartTime)
          );
        } else {
          affectedReservations = [];
        }
      }

      if (affectedReservations.length > 0) {
        impactedReservations = await transformToImpactedReservations(
          affectedReservations,
          'time_changed'
        );

        return {
          hasImpactedReservations: true,
          impactedReservations,
          totalCount: impactedReservations.length,
        };
      }
    }

    // Cas 2: Les jours disponibles changent (et ce n'est pas un créneau "tous les jours")
    if (newAvailableDays.length > 0) {
      let affectedReservations: typeof reservations;

      if (settings.bookingMode === 'time' && newEndTime) {
        // MODE TIME: Chercher les réservations DANS le nouveau créneau sur les jours RETIRÉS
        affectedReservations = reservations.filter(r => {
          const dayOfWeek = new Date(r.date).getDay();
          const wasInOldDays = !oldAvailableDays || oldAvailableDays.length === 0 || oldAvailableDays.includes(dayOfWeek);
          const isInNewDays = newAvailableDays.includes(dayOfWeek);
          const isInSlot = isTimeWithinSlot(r.start_time, newStartTime, newEndTime);
          return isInSlot && wasInOldDays && !isInNewDays;
        });
      } else {
        // MODE SLOT: Chercher les réservations au début exact sur les jours RETIRÉS
        affectedReservations = reservations.filter(r => {
          const dayOfWeek = new Date(r.date).getDay();
          const wasInOldDays = !oldAvailableDays || oldAvailableDays.length === 0 || oldAvailableDays.includes(dayOfWeek);
          return normalizeTimeForUI(r.start_time) === normalizeTimeForUI(newStartTime) && wasInOldDays && !newAvailableDays.includes(dayOfWeek);
        });
      }

      if (affectedReservations.length > 0) {
        impactedReservations = await transformToImpactedReservations(
          affectedReservations,
          'day_unavailable'
        );

        return {
          hasImpactedReservations: true,
          impactedReservations,
          totalCount: impactedReservations.length,
        };
      }
    }

    return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
  } catch (error) {
    console.error('Error checking time slot modification impact:', error);
    return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
  }
}

/**
 * Vérifie l'impact de la suppression d'un créneau horaire
 * 
 * MODE TIME: Détecte les réservations dont l'heure est DANS le créneau
 * MODE SLOT: Détecte les réservations dont l'heure correspond exactement au début
 */
export async function checkTimeSlotDeletionImpact(
  slotId: string,
  startTime: string,
  establishmentId?: string,
  endTime?: string // Nouveau paramètre pour le mode TIME
): Promise<TimeSlotImpact> {
  try {
    const settings = await getReservationSettings(establishmentId);
    const reservations = await getFutureReservations(establishmentId);

    if (!reservations || reservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
    }

    let affectedReservations: typeof reservations;

    if (settings.bookingMode === 'time' && endTime) {
      // MODE TIME: Chercher les réservations DANS le créneau
      affectedReservations = reservations.filter(r => 
        isTimeWithinSlot(r.start_time, startTime, endTime)
      );
    } else {
      // MODE SLOT: Chercher les réservations au début exact du créneau
      affectedReservations = reservations.filter(r => 
        normalizeTimeForUI(r.start_time) === normalizeTimeForUI(startTime)
      );
    }

    if (affectedReservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
    }

    const impactedReservations = await transformToImpactedReservations(
      affectedReservations,
      'time_changed'
    );

    return {
      hasImpactedReservations: true,
      impactedReservations,
      totalCount: impactedReservations.length,
    };
  } catch (error) {
    console.error('Error checking time slot deletion impact:', error);
    return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
  }
}

/**
 * Vérifie l'impact de la désactivation d'un créneau horaire
 * 
 * MODE TIME: Détecte les réservations dont l'heure est DANS le créneau
 * MODE SLOT: Détecte les réservations dont l'heure correspond exactement au début
 */
export async function checkTimeSlotDeactivationImpact(
  slotId: string,
  startTime: string,
  establishmentId?: string,
  endTime?: string // Nouveau paramètre pour le mode TIME
): Promise<TimeSlotImpact> {
  try {
    const settings = await getReservationSettings(establishmentId);
    const reservations = await getFutureReservations(establishmentId);

    if (!reservations || reservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
    }

    let affectedReservations: typeof reservations;

    if (settings.bookingMode === 'time' && endTime) {
      // MODE TIME: Chercher les réservations DANS le créneau
      affectedReservations = reservations.filter(r => 
        isTimeWithinSlot(r.start_time, startTime, endTime)
      );
    } else {
      // MODE SLOT: Chercher les réservations au début exact du créneau
      affectedReservations = reservations.filter(r => 
        normalizeTimeForUI(r.start_time) === normalizeTimeForUI(startTime)
      );
    }

    if (affectedReservations.length === 0) {
      return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
    }

    const impactedReservations = await transformToImpactedReservations(
      affectedReservations,
      'slot_inactive'
    );

    return {
      hasImpactedReservations: true,
      impactedReservations,
      totalCount: impactedReservations.length,
    };
  } catch (error) {
    console.error('Error checking time slot deactivation impact:', error);
    return { hasImpactedReservations: false, impactedReservations: [], totalCount: 0 };
  }
}
